import type { AdminRefundListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId, shortProviderRefundId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatCurrency, formatDate } from '../../../utils/format';
import { RefundStatusBadge } from '../_components/RefundStatusBadge';

/**
 * Column definitions for the refunds history DataGrid (AUT-129), extracted
 * from the page so the page component stays under the line cap. Sorting is
 * disabled per column — the backend list is fixed `createdAt DESC`.
 *
 * **Built to the WIRE, not the mockup (architect guardrail #1 — Igor
 * build-to-contract precedent):** the mockup shows two PHANTOM columns the
 * `AdminRefundListItemDto` wire lacks — DROPPED here:
 *   - `№ R-####` synthetic number → the wire has no counter; the row
 *     identifier is the truncated `providerRefundId` (`rfnd_a91c…03f`).
 *   - `Обработал` / "processed by" → the wire has NO `processedBy` (the
 *     audit log is the actor authority). A `Статус` column (completed /
 *     failed, derived from `processedAt`) takes its place.
 *
 * Identity cell (recipient — the paying customer) renders through
 * `<RedactedField perm="view_pii">`, which agrees with `row.piiRevealed`
 * (server is the sole redaction authority). A revealed value is never
 * re-redacted; only revealed values get a copy button.
 */

/** Recipient cell: avatar (initials) + name (PII-gated). */
const RecipientCell = ({ row, t }: { row: AdminRefundListItemDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: 'primary.light',
        color: 'primary.dark',
      }}
    >
      {initials(row.recipientName)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      {row.recipientName ? (
        <Typography variant="body2" fontWeight={600} noWrap component="span">
          <RedactedField
            perm="view_pii"
            value={row.recipientName}
            redacted={row.recipientName}
            {...(row.piiRevealed ? { copyable: true } : {})}
          />
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
          {t('refunds.noValue')}
        </Typography>
      )}
    </Box>
  </Box>
);

/** Refund cell: truncated providerRefundId (the row identifier — NO R-####). */
const RefundCell = ({ row }: { row: AdminRefundListItemDto }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap>
      {shortProviderRefundId(row.providerRefundId)}
    </Typography>
  </Box>
);

export const buildRefundColumns = (t: TFunction): GridColDef<AdminRefundListItemDto>[] => [
  {
    field: 'refund',
    headerName: t('refunds.columns.refund'),
    flex: 0.9,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => <RefundCell row={row} />,
  },
  {
    field: 'order',
    headerName: t('refunds.columns.order'),
    flex: 0.7,
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace" sx={{ color: tokens.text.secondary }}>
        {shortId(row.orderId)}
      </Typography>
    ),
  },
  {
    field: 'recipient',
    headerName: t('refunds.columns.recipient'),
    flex: 1.2,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => <RecipientCell row={row} t={t} />,
  },
  {
    field: 'amount',
    headerName: t('refunds.columns.amount'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
        {formatCurrency(Number(row.amount))}
      </Typography>
    ),
  },
  {
    field: 'reason',
    headerName: t('refunds.columns.reason'),
    flex: 1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => (
      <Chip size="small" variant="outlined" label={t(`refunds.reason.${row.reason}`)} />
    ),
  },
  {
    field: 'status',
    headerName: t('refunds.columns.status'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => <RefundStatusBadge status={row.status} />,
  },
  {
    field: 'createdAt',
    headerName: t('refunds.columns.created'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {formatDate(row.createdAt)}
      </Typography>
    ),
  },
];
